'use client'

/**
 * Purpose: Snapshot camera orchestrator — idle, live camera, optional held preview.
 * Inputs: CameraCaptureProps (labels, value/onChange or onCapture).
 * Outputs: User-selected JPEG base64 via onChange or onCapture.
 */

import { useCallback, useEffect, useState } from 'react'
import type { CameraCaptureProps } from '@lib/models/interfaces'
import { useCameraStream } from '@/hooks/use-camera-stream'
import { useImageFilePicker } from '@/hooks/use-image-file-picker'
import { useVideoFrameCapture } from '@/hooks/use-video-frame-capture'
import { CameraPreview } from '@components/captures/Camera/CameraPreview'
import { CameraActions } from '@components/captures/Camera/CameraActions'
import './Camera.css'

// idle = picker UI; camera = live preview awaiting shutter.
type Stage = 'idle' | 'camera'

// Supports controlled (value/onChange) and one-shot (onCapture) usage patterns.
export function CameraCapture({
  id,
  facingMode,
  error: externalError,
  onError,
  disabled,
  className,
  labels,
  value = null,
  onChange,
  onCapture,
  validateBase64,
  onValidationError,
  onStreamError,
  onFrameCaptureFailed,
  showPreviewWhenControlled = true,
}: CameraCaptureProps) {
  const [stage, setStage] = useState<Stage>('idle')
  // Merge parent-provided errors with local stream / validation failures.
  const [internalError, setInternalError] = useState<string | null>(null)
  const displayError = externalError ?? internalError

  const clearError = useCallback(() => setInternalError(null), [])

  const { videoRef, start, stop, isActive } = useCameraStream({
    facingMode,
    onError: (reason, err) => {
      onStreamError?.(reason, err)
    },
  })

  const { captureFrame } = useVideoFrameCapture()

  // Shared path for file upload and shutter — runs optional size validation first.
  const applyBase64 = useCallback(
    async (base64: string, source: 'camera' | 'file') => {
      if (validateBase64 && !validateBase64(base64)) {
        onValidationError?.()
        return
      }
      if (onChange) {
        onChange(base64)
      }
      onCapture?.(base64, source)
    },
    [onCapture, onChange, onValidationError, validateBase64],
  )

  // Disk picker shares validation and onChange path with the shutter capture.
  const { inputRef, open, handleChange } = useImageFilePicker({
    onPick: async (base64) => {
      clearError()
      // Leave camera stage so the held preview or idle UI can show.
      setStage('idle')
      await applyBase64(base64, 'file')
    },
    onError: (err) => onError?.(err instanceof Error ? err.message : String(err)),
    validateBase64,
    onValidationError,
  })

  // Transition to camera stage — getUserMedia starts in the effect below.
  const handleStartCamera = useCallback(() => {
    if (disabled) return
    clearError()
    setStage('camera')
  }, [clearError, disabled])

  // Start getUserMedia once stage transitions to camera and stream is not active.
  useEffect(() => {
    if (stage !== 'camera' || isActive) return
    let cancelled = false
    void start().catch(() => {
      if (!cancelled) setStage('idle')
    })
    return () => {
      cancelled = true
    }
  }, [isActive, stage, start])

  // Grab one JPEG frame, tear down the stream, and emit base64 upstream.
  const handleCapturePhoto = useCallback(async () => {
    const video = videoRef.current
    const base64 = await captureFrame(video)
    if (!base64) {
      onFrameCaptureFailed?.()
      return
    }
    stop()
    setStage('idle')
    await applyBase64(base64, 'camera')
  }, [applyBase64, captureFrame, onFrameCaptureFailed, onValidationError, stop, videoRef])

  // User cancelled — tear down stream without persisting a frame.
  const handleCancelCamera = useCallback(() => {
    stop()
    setStage('idle')
  }, [stop])

  // Controlled clear — drop held preview and return to idle picker.
  const handleClear = useCallback(() => {
    onChange?.(null)
    setStage('idle')
  }, [onChange])

  // Retake reopens the camera; remove keeps the form on idle without a preview.
  const heldPreviewActions = [
    {
      id: `${id ?? 'camera'}-retake`,
      label: labels.retake ?? labels.capture,
      onClick: () => {
        handleClear()
        void handleStartCamera()
      },
    },
    // Remove clears the held base64 without opening the camera again.
    {
      id: `${id ?? 'camera'}-remove`,
      label: labels.remove ?? labels.cancel,
      variant: 'outline' as const,
      onClick: handleClear,
    },
  ]

  // Shutter + cancel while the live stream is active.
  const liveCameraActions = [
    {
      id: `${id ?? 'camera'}-cancel`,
      label: labels.cancel,
      variant: 'outline' as const,
      onClick: handleCancelCamera,
    },
    {
      id: `${id ?? 'camera'}-take-photo`,
      label: labels.takePhoto,
      onClick: () => void handleCapturePhoto(),
    },
  ]

  // Open camera or pick a file when no preview is held.
  const idleActions = [
    {
      id: `${id ?? 'camera'}-open`,
      label: labels.capture,
      onClick: () => void handleStartCamera(),
      disabled,
    },
    {
      id: `${id ?? 'camera'}-upload`,
      label: labels.upload,
      variant: 'outline' as const,
      onClick: open,
      disabled,
    },
  ]

  // Controlled mode shows held preview with retake/remove when value is set.
  if (showPreviewWhenControlled && value) {
    return (
      <div id={id} className={className}>
        {labels.label ? <label className="text-black/60 text-sm">{labels.label}</label> : null}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`data:image/jpeg;base64,${value}`}
          alt={labels.previewAlt ?? ''}
          className={`camera-held-preview ${facingMode === 'user' ? 'camera-held-preview--mirror' : ''}`}
        />
        <CameraActions actions={heldPreviewActions} />
      </div>
    )
  }

  // Live camera stage — preview plus cancel / shutter actions.
  if (stage === 'camera') {
    return (
      <div id={id} className={className}>
        {labels.label ? <label className="text-black/60 text-sm">{labels.label}</label> : null}
        <CameraPreview videoRef={videoRef} mirror={facingMode === 'user'} />
        {isActive ? (
          <CameraActions className="mt-4" actions={liveCameraActions} />
        ) : null}
      </div>
    )
  }

  // Idle stage — open camera or pick an image from disk.
  return (
    <div id={id} className={className}>
      {labels.label ? <label className="text-black/60 text-sm">{labels.label}</label> : null}
      {labels.hint ? <p className="camera-hint">{labels.hint}</p> : null}
      <p className="camera-hint">{labels.sizeHint}</p>
      {displayError ? <p className="camera-error">{displayError}</p> : null}
      <CameraActions actions={idleActions} />
      <input ref={inputRef} type="file" accept="image/*" className="camera-file-input" onChange={handleChange} />
    </div>
  )
}
